import Papa from 'papaparse';
import { errorToast } from 'lib/components/toasts/error';

export interface StyleObject {
  container?: React.CSSProperties;
}

export interface FileInfo {
  name: string;
  size: number;
  type: string;
}

// This needs to be changed whenever new file types are supported
export type FileLoadedData =
  | string // Base64 (images) or Blob URL (audio)
  | ArrayBuffer // fallback
  | any[][] // CSV raw
  | Record<string, any>[]; // CSV with headers

export type AcceptedFileExtension =
  | '.png'
  | '.jpg'
  | '.jpeg'
  | '.mp3'
  | '.csv'
  | 'image/*'
  | 'audio/*';

type FileHandler = (
  file: File,
  info: FileInfo,
  reader: FileReader,
  onFileLoaded?: (data: FileLoadedData, fileInfo: FileInfo) => void,
  encoding?: string,
  setPreview?: React.Dispatch<React.SetStateAction<string | null>>
) => void;

export const fileTypeMapper: Record<string, FileHandler> = {
  csv: (file, info, reader, onFileLoaded, encoding = 'UTF-8') => {
    reader.onload = () => {
      try {
        const normalized = (reader.result as string).replace(/\r\n/g, '\n');
        const parsed = Papa.parse(normalized, {
          header: true,
          encoding,
          error: () => errorToast({ title: `Error parsing CSV file.` }),
        });
        onFileLoaded?.(parsed.data as FileLoadedData, info);
      } catch {
        errorToast({ title: `Error reading CSV file.` });
      }
    };
    reader.readAsText(file, encoding);
  },

  image: (file, info, reader, onFileLoaded, _encoding, setPreview) => {
    reader.onload = () => {
      const base64 = reader.result as string;
      setPreview?.(base64);
      onFileLoaded?.(base64, info);
    };
    reader.readAsDataURL(file);
  },

  audio: (file, info, _reader, onFileLoaded, _encoding, setPreview) => {
    const blobUrl = URL.createObjectURL(file);
    setPreview?.(blobUrl);
    onFileLoaded?.(blobUrl, info);
  },

  default: (file, info, reader, onFileLoaded) => {
    reader.onload = () => {
      if (reader.result !== null) {
        onFileLoaded?.(reader.result as FileLoadedData, info);
      } else {
        errorToast({ title: `File reading failed: result is null.` });
      }
    };
    reader.readAsArrayBuffer(file);
  },
};
